import { APIHostCheck } from "./hostchecks"
import { TestState, TestGroup, TestDisplayer } from "./common"

/* The parameters returned from the "test-endpoints" API endpoint */
export interface APIHostTestParameters {
    host: string;
    location: string;
    wspath?: string;
    stpath?: string;
}
interface CoordinatorTestResponse {
    servers: Array<APIHostTestParameters>;
    expect_more?: number;
}

/* This could easily be moved inside the controller-request closure, but it's sometimes convenient to be able to inspect it in the browser console */
const testsByLocation: Map<string, APIHostCheck> = new Map();

function detailForFetchException(exc: Error, ref: string) {
    let msg: string;
    if (typeof(exc) === 'object' && (exc instanceof TypeError)) {
        /* For whatever reason, the opaque error returned by CORS failures is a TypeError (according to the Fetch LS at whatwg). */
        msg = exc.message;
    } else {
        console.log(exc);
        msg = exc.toString().concat(".");
    }

    return [ { 'elt': 'p', value: msg }, detailmsg(ref) ];
}

function detailForFetchResponse(response: Response, ref: string) {
    return [ {'elt': 'p', 'value': 'HTTP response: ' + response.status + ' ' + response.statusText },
             detailmsg(ref) ];
}

function detailmsg(uri: string) {
    return {'elt': 'p', 'value': [ 'Check the browser console for errors regarding ', { 'cls': 'urltext', 'value': uri }, "." ] };
}

/* Sanity-check the coordinator response */
function validateTestParameters(sinfo: APIHostTestParameters): boolean {
    if (typeof(sinfo.host) !== 'string' || typeof(sinfo.location) !== 'string') {
        return false
    }

    const hostname_re = /^[a-z][a-z0-9_-]*(?:\.[a-z][a-z0-9_-]*)+$/;
    if (!hostname_re.test(sinfo.host)) {
        return false
    }

    const path_re = /^(?:\/[^/\s?;]+)+$/;
    if (sinfo.wspath !== undefined && !path_re.test(sinfo.wspath)) {
        return false
    }
    if (sinfo.stpath !== undefined && !path_re.test(sinfo.stpath)) {
        return false
    }

    return true
}

async function checkCoordinatorAccess(disp: TestGroup) {
    const coordDisp = new TestDisplayer(disp, '-coordinator-')

    coordDisp.noteShort("Coordinator")

    coordDisp.noteStatus(
        TestState.InProgress,
        "Checking if we can reach the coordinator\u2026")

    disp.addChild(coordDisp)

    const coordinatorHostname = process.env.REACT_APP_COORDINATOR_HOSTNAME!;
    const coordinator = `https://${coordinatorHostname}/`;
    const fallbackTestServers: APIHostTestParameters[] = [];

    const url = coordinator + 'api/0/test-endpoints';

    let response;
    try {
        response = await fetch(url, { mode: 'cors' });
    } catch (err) {
        console.log(err);
        coordDisp.noteStatus(TestState.Fatal, 'Unable to reach the coordinator.');
        coordDisp.noteDetail(...detailForFetchException(err, coordinator))
        checkLocations(fallbackTestServers, disp);
        return;
    }

    if (!response.ok) {
        coordDisp.noteStatus(TestState.Fatal, 'Error communicating with coordinator.');
        coordDisp.noteDetail(...detailForFetchResponse(response, coordinator))
        checkLocations(fallbackTestServers, disp);
        return;
    }

    let testpoints: CoordinatorTestResponse
    try {
        testpoints = (await response.json()) as CoordinatorTestResponse;
        console.log(testpoints);
        checkLocations(testpoints['servers'], disp);
        coordDisp.noteStatus(TestState.InProgress, 'We can communicate with the coordinator.');
    } catch (err) {
        console.log(err);
        coordDisp.noteStatus(TestState.Fatal, 'Garbled response from coordinator.');
        coordDisp.noteDetail(...detailForFetchException(err, coordinator))
        return;
    }

    try {
        await scheduleMore(testpoints.expect_more, url, disp);
    } catch (err) {
        console.log(err);
        coordDisp.noteStatus(TestState.Uncertain, 'Error fetching additional info.');
        return;
    }
    coordDisp.noteStatus(TestState.Ok, 'We can communicate with the coordinator.');
}

/* This is called each time we get an updated set of test parameters from the
   coordinator; it merges the new set into any already-running checks */
function checkLocations(locations: Array<APIHostTestParameters>, displayer: TestGroup) {
    locations.forEach(
        (sinfo) => {
            const existing = testsByLocation.get(sinfo.location)
            if (existing !== undefined) {
                existing.updateParameters(sinfo)
            } else {
                const check = new APIHostCheck(displayer, sinfo.location, sinfo.host);
                testsByLocation.set(sinfo.location, check)
                displayer.addChild(check)
                check.updateParameters(sinfo)
            }
        }
    )
}

function scheduleMore(delay: number | undefined, url: string, group: TestGroup): Promise<void> {
    const beganTime = Date.now();
    let fetchCount = 0;

    /* If nextdelay is non-null, schedule another check at that time; otherwise resolve */
    function schedule(nextdelay: number | undefined, resolve: () => void, reject: (e: Error) => void) {
        if (nextdelay === undefined) {
            resolve()
            return
        }

        if (fetchCount > 10 || (Date.now() - beganTime) > (30 * 1000)) {
            console.log('(Not fetching more updates.)')
            resolve()
            return
        }

        if (nextdelay < 0.5 * (1 + fetchCount)) {
            nextdelay = 0.5 * (1 + fetchCount);
        }

        console.log('Fetching more after delay:', nextdelay)

        const msec = nextdelay * 1000
        window.setTimeout(() => {
            askAgain().then(
                (testpoints: CoordinatorTestResponse) => {
                    checkLocations(testpoints['servers'], group)
                    schedule(testpoints.expect_more, resolve, reject)
                },
                reject
            )
        }, msec)
    }

    /* Request updated info from the coordinator */
    function askAgain(): Promise<CoordinatorTestResponse> {
        fetchCount += 1;

        console.log("Refetch #", fetchCount);

        return fetch(url, { mode: 'cors' })
            .then( (resp) => {
                console.log('coordinator (update):', resp.statusText)
                if(resp.ok) {
                    return resp.json()
                } else {
                    return Promise.reject()
                }
            })
    }

    return new Promise( (re, rj) => schedule(delay, re, rj) );
}

export { detailForFetchException, detailForFetchResponse,
    validateTestParameters, checkCoordinatorAccess,
    detailmsg }
